import { motion } from 'framer-motion';
import { Zap, Flame, Music, Eye, Sparkles, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';

const RoxxieVayne = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  return (
    <div className="bg-void-black min-h-screen">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" className="font-syne font-extrabold text-xl tracking-tighter uppercase">
            <span className="text-neon-red">Shadowcell</span> Studios
          </Link>
          <Link 
            to="/"
            className="font-manrope font-medium text-sm tracking-widest uppercase text-neutral-300 hover:text-neon-red transition-colors duration-300"
          >
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_23aab0a6-b4d8-4ab8-9e19-4bd12735db59/artifacts/hl9bk67a_hf_20260204_232634_37115a9c-b63c-4e57-a680-5806bd5a9da5.png')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void-black/60 via-void-black/40 to-void-black" />
        <div className="absolute inset-0 hero-glow" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <p className="font-manrope text-xs md:text-sm tracking-[0.4em] uppercase text-neon-red mb-4">
              ShadowCell Studios Creation
            </p>
            <h1 className="font-syne font-extrabold text-7xl md:text-9xl tracking-tighter uppercase leading-[0.85] text-white">
              Roxxie<br />Vayne
            </h1>
            <p className="font-manrope text-neutral-300 text-sm md:text-base tracking-wide mt-6 max-w-xl mx-auto">
              Glam-Metal Frontwoman • Cinematic Rock Icon • Platinum Puma Lead
            </p>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <Zap className="w-6 h-6 text-neon-red" />
        </motion.div>
      </section>

      {/* Intro Quote */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center"
            {...fadeInUp}
          >
            <p className="font-syne font-bold text-3xl md:text-5xl text-white leading-tight">
              "I don't step onto a stage.<br />
              <span className="text-neon-red">I switch it on.</span>"
            </p>
            <p className="font-manrope text-neutral-400 mt-8 text-lg leading-relaxed max-w-2xl mx-auto">
              I exist where sound turns physical and light learns how to move.
              Every frame bends. Every amp strains. Every crowd locks in.
              <span className="text-neon-red font-medium"> This isn't nostalgia. It's ignition.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who I Am */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            {...fadeInUp}
          >
            <div>
              <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4 flex items-center gap-2">
                <Crown className="w-4 h-4" />
                Who I Am
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-white mb-8">
                The Identity
              </h2>
              
              <div className="space-y-4 font-manrope text-neutral-300">
                <div className="glass p-4 rounded-lg">
                  <span className="text-neon-red font-bold">Name:</span> Roxxie Vayne
                </div>
                <div className="glass p-4 rounded-lg">
                  <span className="text-neon-red font-bold">Age:</span> Mid-30s
                </div>
                <div className="glass p-4 rounded-lg">
                  <span className="text-neon-red font-bold">Role:</span> Female rock frontwoman • Glam-metal icon • Cinematic lead
                </div>
                <div className="glass p-4 rounded-lg">
                  <span className="text-neon-red font-bold">Band:</span> Platinum Puma (80s glam metal)
                </div>
                <div className="glass p-4 rounded-lg">
                  <span className="text-neon-red font-bold">Created by:</span> ShadowCell Studios
                </div>
              </div>
            </div>
            
            <div className="glass p-8 rounded-lg border-glow">
              <p className="font-manrope text-neutral-300 text-lg leading-relaxed">
                I'm the frontwoman and visual core of <span className="text-neon-red font-bold">Platinum Puma</span>, 
                an 80s glam metal band forged by ShadowCell Studios.
              </p>
              <p className="font-manrope text-neutral-300 text-lg leading-relaxed mt-4">
                Platinum Puma isn't retro cosplay. <span className="text-white font-medium">It's a resurrection with teeth.</span>
              </p>
              <p className="font-manrope text-neutral-300 text-lg leading-relaxed mt-4">
                Big hooks. Dangerous shine. Stadium-sized attitude.
              </p>
              <p className="font-syne font-bold text-2xl text-white mt-6">
                I don't just sing the songs. <span className="text-neon-red">I detonate them.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Look */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <div className="text-center mb-16">
              <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4 flex items-center justify-center gap-2">
                <Eye className="w-4 h-4" />
                The Look
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-white">
                Visual Identity
              </h2>
              <p className="font-syne font-bold text-xl text-neon-red mt-4">
                Glam with bite. Polish sharpened into a weapon.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="glass p-6 rounded-lg text-center card-lift">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-600 to-amber-900 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-syne font-bold text-lg text-white mb-2">Skin</h3>
                <p className="font-manrope text-neutral-400 text-sm">
                  Sun-tanned, glowing under neon, sparks, and flame
                </p>
              </div>
              
              <div className="glass p-6 rounded-lg text-center card-lift">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-green-600 flex items-center justify-center">
                  <Eye className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-syne font-bold text-lg text-white mb-2">Eyes</h3>
                <p className="font-manrope text-neutral-400 text-sm">
                  Hazel, sharp enough to cut through smoke and lens
                </p>
              </div>
              
              <div className="glass p-6 rounded-lg text-center card-lift">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-neutral-800 to-black flex items-center justify-center border border-neutral-600">
                  <span className="text-2xl">〰️</span>
                </div>
                <h3 className="font-syne font-bold text-lg text-white mb-2">Hair</h3>
                <p className="font-manrope text-neutral-400 text-sm">
                  Jet-black curls, teased wild, alive with motion
                </p>
              </div>
              
              <div className="glass p-6 rounded-lg text-center card-lift">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-600 to-purple-800 flex items-center justify-center">
                  <span className="text-2xl">💄</span>
                </div>
                <h3 className="font-syne font-bold text-lg text-white mb-2">Makeup</h3>
                <p className="font-manrope text-neutral-400 text-sm">
                  High-contrast. Dark liner, metallic shadow, crimson lips
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Armor */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <div className="text-center mb-16">
              <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4">
                The Armor
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-white">
                Built for Stages
              </h2>
              <p className="font-manrope text-neutral-400 mt-4">
                Studios, and cinematic destruction.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass p-8 rounded-lg border-l-4 border-neon-red">
                <h3 className="font-syne font-bold text-2xl text-white mb-3">Corset</h3>
                <p className="font-manrope text-neutral-300">Black leather, sculpted and battle-tight</p>
              </div>
              <div className="glass p-8 rounded-lg border-l-4 border-neon-red">
                <h3 className="font-syne font-bold text-2xl text-white mb-3">Coat</h3>
                <p className="font-manrope text-neutral-300">Long gothic leather, snapping and whipping with movement</p>
              </div>
              <div className="glass p-8 rounded-lg border-l-4 border-neon-red">
                <h3 className="font-syne font-bold text-2xl text-white mb-3">Boots</h3>
                <p className="font-manrope text-neutral-300">Stiletto heels, lethal elegance</p>
              </div>
              <div className="glass p-8 rounded-lg border-l-4 border-neon-red">
                <h3 className="font-syne font-bold text-2xl text-white mb-3">Choker</h3>
                <p className="font-manrope text-neutral-300">Diamond choker, flashing like a blade under light bursts</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Presence & Energy */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center"
            {...fadeInUp}
          >
            <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4 flex items-center justify-center gap-2">
              <Flame className="w-4 h-4" />
              Presence & Energy
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <span className="font-syne font-extrabold text-4xl md:text-6xl text-white">Commanding.</span>
              <span className="font-syne font-extrabold text-4xl md:text-6xl text-neon-red">Unapologetic.</span>
              <span className="font-syne font-extrabold text-4xl md:text-6xl text-white">Electrifying.</span>
            </div>
            
            <p className="font-manrope text-neutral-300 text-lg leading-relaxed">
              I don't perform for the crowd.<br />
              <span className="text-white font-medium">The crowd moves because Platinum Puma is in the room.</span>
            </p>
            <p className="font-manrope text-neutral-400 text-lg leading-relaxed mt-6">
              Every step lands with intent.<br />
              Every glance feels like a dare you shouldn't take.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How I Move */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <div className="text-center mb-16">
              <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4 flex items-center justify-center gap-2">
                <Zap className="w-4 h-4" />
                How I Move
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-white">
                Power-Walk Confidence
              </h2>
            </div>

            <div className="glass p-8 md:p-12 rounded-lg border-glow">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <p className="font-manrope text-neutral-300 text-lg">
                    Sharp turns. Sudden stillness. Then <span className="text-neon-red font-bold">impact.</span>
                  </p>
                </div>
                <div>
                  <p className="font-manrope text-neutral-300 text-lg">
                    Hair flips locked to drum hits. Hands slicing through smoke.
                  </p>
                </div>
                <div>
                  <p className="font-manrope text-neutral-300 text-lg">
                    Neon reacts. Lights glitch. <span className="text-neon-red font-bold">Reality stutters.</span>
                  </p>
                </div>
              </div>
              <p className="font-syne font-bold text-2xl text-white text-center mt-8">
                The stage behaves like it's alive.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cinematic Aura */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="section-divider w-full absolute top-0" />
        <div className="absolute inset-0 hero-glow opacity-30" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div 
            className="text-center"
            {...fadeInUp}
          >
            <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4">
              Cinematic Aura
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-12">
              <span className="glass px-6 py-3 rounded-full font-manrope text-white">Neon fire</span>
              <span className="glass px-6 py-3 rounded-full font-manrope text-white">Smoke plumes</span>
              <span className="glass px-6 py-3 rounded-full font-manrope text-white">Light flares</span>
            </div>
            
            <p className="font-manrope text-neutral-300 text-xl leading-relaxed">
              When I lock eyes with the camera, <span className="text-neon-red font-bold">systems destabilize.</span>
            </p>
            <p className="font-manrope text-neutral-400 text-lg leading-relaxed mt-4">
              Frames warp. Color blooms.<br />
              Scenes don't capture me. They reorganize around me.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ShadowCell Mythos */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div 
            className="glass p-8 md:p-16 rounded-lg border-glow text-center"
            {...fadeInUp}
          >
            <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-6 flex items-center justify-center gap-2">
              <Music className="w-4 h-4" />
              ShadowCell Mythos
            </p>
            
            <p className="font-syne font-bold text-3xl md:text-4xl text-white mb-6">
              I'm not just a singer.<br />
              <span className="text-neon-red">I'm a signal.</span>
            </p>
            
            <p className="font-manrope text-neutral-300 text-lg leading-relaxed mb-8">
              A living broadcast of rebellion, excess, and amplified sound made physical.
              When Roxxie Vayne steps forward with Platinum Puma, the scene rewrites itself.
              Rules loosen. Volume climbs. Limits disappear.
            </p>
            
            <div className="border-t border-white/10 pt-8">
              <p className="font-manrope text-neutral-400 text-lg">
                ShadowCell Studios doesn't make bands.
              </p>
              <p className="font-syne font-extrabold text-2xl text-white mt-2">
                It engineers <span className="text-neon-red">icons.</span>
              </p>
              <p className="font-manrope text-neutral-300 text-lg mt-4">
                And I'm the one holding the mic when the lights go nuclear.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-syne font-extrabold text-5xl md:text-7xl text-white uppercase tracking-tighter">
              Roxxie Vayne
            </h2>
            <p className="font-manrope text-neutral-400 text-lg mt-4">
              Frontwoman of Platinum Puma<br />
              ShadowCell Studios Creation
            </p>
            <p className="font-syne font-bold text-2xl text-neon-red mt-8">
              Turn it up. Don't look away.
            </p>
            
            <Link 
              to="/"
              className="inline-block mt-12 btn-primary px-8 py-4 rounded-none font-manrope font-bold text-sm tracking-widest uppercase neon-glow-hover"
            >
              Back to ShadowCell Studios
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-manrope text-neutral-500 text-sm">
            © {new Date().getFullYear()} ShadowCell Studios. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RoxxieVayne;
